import { useRef } from 'react';

import { AccountCreation } from '@/components/signup/account-creation/account-creation.tsx';
import { LoomOverlayTemplate } from '@/components/signup/layouts/product-overlay-template.tsx';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href.ts';
import { experiments } from '@/utils/experiment/manifest.ts';
import { useExperiment } from '@/utils/experiment/use-experiment.ts';
import { getWacIntegrationUrl } from '@/utils/urls.ts';

import { GetStartedText } from '../../components/get-started-heading.tsx';
import { Headings } from '../../components/headings.tsx';
import { FreeForTenUsersText } from '../../components/sub-heading.tsx';
import { InputSubmitButton } from '../../components/submit-input.tsx';

export const LoggedOutLoom = () => {
	const loomLandOptIn = useExperiment(experiments.twcg2108LoomLandOptIn);
	const choosePath = useLocalizedHref('/try/cloud/loom/choose');

	// getContinueUrl is invoked by AccountCreation on both email submit and social
	// click, and onSubmit resets its submitting state on error (allowing retries).
	// Guard exposure with a ref so it fires at most once per page load regardless
	// of how many times the user submits.
	const hasExposureFired = useRef(false);

	// This surface owns exposure for the TWCG-2108 opt-in experiment: exposure
	// fires only when the user creates an account (email submit / social click),
	// via AccountCreation's getContinueUrl. Treatment cohorts route to /loom/choose
	// (which reads the resolved cohort to render the arm); control returns undefined
	// so AccountCreation falls through to today's Loom-only flow. The continue URL
	// must be absolute — getWacIntegrationUrl() supplies the bare WAC origin, and we
	// forward the current signup query params so intent/bundle context is preserved.
	const getContinueUrl = (): string | undefined => {
		if (loomLandOptIn.type === 'enrolled' && loomLandOptIn.usingDefault === false) {
			if (!hasExposureFired.current) {
				loomLandOptIn.fireExposure();
				hasExposureFired.current = true;
			}
			if (loomLandOptIn.value !== 'control') {
				return `${getWacIntegrationUrl()}${choosePath}${window.location.search}`;
			}
		}
		return undefined;
	};

	return (
		<LoomOverlayTemplate size="sm">
			<Headings main={<GetStartedText productName="Loom" />} sub={<FreeForTenUsersText />} />
			<AccountCreation
				getContinueUrl={getContinueUrl}
				Input={<InputSubmitButton />}
				Submit={null}
			/>
		</LoomOverlayTemplate>
	);
};
